import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Calendar, GraduationCap, Award, Code } from 'lucide-react';
import { theme, Badge } from '../styles/theme';
import { Section } from '../components/ui/Section';
import { SEO } from '../components/SEO';
import { StructuredData } from '../components/StructuredData';
import { experiences, education, skills, languages, interests } from '../data/portfolio';

const AboutHero = styled.section`
  padding: ${theme.spacing['3xl']} 0;
  background: ${theme.colors.backgroundAlt};
  background-image: radial-gradient(circle at 20% 20%, rgba(58, 123, 213, 0.04) 0%, transparent 50%);
`;

const HeroContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.lg};
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: ${theme.spacing['3xl']};
  align-items: center;

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const ProfileImage = styled.img`
  width: 300px;
  height: 300px;
  border-radius: ${theme.borderRadius.full};
  object-fit: cover;
  margin: 0 auto;
  box-shadow: ${theme.shadows.xl};
  border: 4px solid ${theme.colors.border};

  @media (max-width: ${theme.breakpoints.md}) {
    width: 250px;
    height: 250px;
  }
`;

const HeroContent = styled.div``;

const HeroTitle = styled.h1`
  font-size: ${theme.fontSizes['4xl']};
  font-weight: 700;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.fontSizes['3xl']};
  }
`;

const HeroSubtitle = styled.h2`
  font-size: ${theme.fontSizes.xl};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.lg};
  font-weight: 600;
`;

const HeroDescription = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.textLight};
  line-height: 1.7;
  margin-bottom: ${theme.spacing.xl};
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  color: ${theme.colors.primary};
`;

const StatLabel = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textLight};
  margin-top: ${theme.spacing.xs};
`;

const TimelineContainer = styled.div`
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 20px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: ${theme.colors.border};
  }
`;

const TimelineItem = styled(motion.div)`
  position: relative;
  padding-left: ${theme.spacing['3xl']};
  margin-bottom: ${theme.spacing['2xl']};

  &::before {
    content: '';
    position: absolute;
    left: 11px;
    top: 8px;
    width: 18px;
    height: 18px;
    border-radius: ${theme.borderRadius.full};
    background: ${theme.colors.primary};
    border: 3px solid ${theme.colors.background};
    box-shadow: 0 0 0 3px ${theme.colors.border};
  }
`;

const TimelineContent = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  border: 1px solid ${theme.colors.border};
  transition: all 0.15s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${theme.spacing.sm};
  }
`;

const TimelineTitle = styled.h3`
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const TimelineSubtitle = styled.h4`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.primary};
  font-weight: 500;
`;

const TimelineDate = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  color: ${theme.colors.textLight};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
`;

const TimelineDescription = styled.ul`
  margin-bottom: ${theme.spacing.lg};
`;

const TimelineDescItem = styled.li`
  color: ${theme.colors.textLight};
  line-height: 1.6;
  margin-bottom: ${theme.spacing.sm};
  position: relative;
  padding-left: ${theme.spacing.lg};

  &::before {
    content: '•';
    color: ${theme.colors.primary};
    position: absolute;
    left: 0;
    font-weight: bold;
  }
`;

const TechnologiesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing.xl};
`;

const SkillCategory = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.xl};
  border: 1px solid ${theme.colors.border};
`;

const SkillCategoryTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.lg};
  text-transform: capitalize;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const SkillsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const SkillItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SkillName = styled.span`
  font-weight: 500;
  color: ${theme.colors.text};
`;

const SkillLevel = styled.span<{ level: string }>`
  font-size: ${theme.fontSizes.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-weight: 500;
  
  ${({ level }) => {
    switch (level) {
      case 'expert':
        return `background: ${theme.colors.success}20; color: ${theme.colors.success};`;
      case 'advanced':
        return `background: ${theme.colors.primary}20; color: ${theme.colors.primary};`;
      case 'intermediate':
        return `background: ${theme.colors.accent}20; color: ${theme.colors.accent};`;
      default:
        return `background: ${theme.colors.secondary}20; color: ${theme.colors.secondary};`;
    }
  }}
`;

const LanguagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.lg};
`;

const LanguageCard = styled(motion.div)`
  background: ${theme.colors.backgroundAlt};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  text-align: center;
  transition: all 0.15s ease;
  
  &:hover {
    border-color: ${theme.colors.primary};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const LanguageName = styled.h3`
  color: ${theme.colors.text};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin-bottom: ${theme.spacing.sm};
`;

const LanguageLevel = styled.div`
  display: inline-block;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background: ${theme.colors.primary}20;
  color: ${theme.colors.primary};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
  margin-bottom: ${theme.spacing.sm};
`;

const LanguageDescription = styled.p`
  color: ${theme.colors.textLight};
  font-size: ${theme.fontSizes.sm};
`;

const InterestsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing.lg};
`;

const InterestCard = styled(motion.div)`
  background: ${theme.colors.backgroundAlt};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  transition: all 0.15s ease;
  
  &:hover {
    border-color: ${theme.colors.primary};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const InterestName = styled.h3`
  color: ${theme.colors.text};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin-bottom: ${theme.spacing.sm};
`;

const InterestDescription = styled.p`
  color: ${theme.colors.textLight};
  font-size: ${theme.fontSizes.sm};
  line-height: 1.6;
`;

export const About: React.FC = () => {
  const skillsByCategory = skills.reduce((acc, skill) => {
    if (!acc[skill.category]) {
      acc[skill.category] = [];
    }
    acc[skill.category].push(skill);
    return acc;
  }, {} as Record<string, typeof skills>);

  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://your-domain.vercel.app';

  return (
    <>
      <SEO
        title="About"
        description="Full-stack developer with 3+ years experience specializing in React, TypeScript, C#, and .NET. ZHAW Computer Science student with proven track record at Swiss National Bank and academic research projects."
        canonical={`${siteUrl}/about`}
      />
      <StructuredData 
        type="page" 
        breadcrumbs={[
          { name: 'Home', url: siteUrl },
          { name: 'About', url: `${siteUrl}/about` }
        ]}
      />
      
      <AboutHero>
        <HeroContainer>
          <ProfileImage src="/profile_pic.png" alt="Antonio Michal Verdile - Software Engineer" loading="eager" />
          
          <HeroContent>
            <HeroTitle>About Me</HeroTitle>
            <HeroSubtitle>Full Stack Developer & Software Engineer</HeroSubtitle>
            <HeroDescription>
              Dedicated full-stack developer with comprehensive experience in modern web technologies and enterprise software development. 
              Specialized in building scalable applications using React, TypeScript, C#, and .NET frameworks. Passionate about solving complex technical challenges 
              and delivering high-quality, user-centric solutions that drive business value.
            </HeroDescription>
            
            <StatsContainer>
              <StatItem>
                <StatNumber>3+</StatNumber>
                <StatLabel>Years Experience</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>7+</StatNumber>
                <StatLabel>Projects Completed</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>12+</StatNumber>
                <StatLabel>Technologies</StatLabel>
              </StatItem>
            </StatsContainer>
          </HeroContent>
        </HeroContainer>
      </AboutHero>

      <Section title="Professional Experience" subtitle="Career progression and professional accomplishments">
        <TimelineContainer>
          {experiences.map((exp, index) => (
            <TimelineItem
              key={exp.id}
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <TimelineContent>
                <TimelineHeader>
                  <div>
                    <TimelineTitle>{exp.position}</TimelineTitle>
                    <TimelineSubtitle>{exp.company}</TimelineSubtitle>
                  </div>
                  <TimelineDate>
                    <Calendar size={16} />
                    {exp.startDate} - {exp.isCurrentJob ? 'Present' : exp.endDate}
                  </TimelineDate>
                </TimelineHeader>
                
                <TimelineDescription>
                  {exp.description.map((item, idx) => (
                    <TimelineDescItem key={idx}>{item}</TimelineDescItem>
                  ))}
                </TimelineDescription>

                <TechnologiesContainer>
                  {exp.technologies.map((tech, techIdx) => (
                    <Badge key={techIdx} variant="primary">
                      {tech}
                    </Badge>
                  ))}
                </TechnologiesContainer>
              </TimelineContent>
            </TimelineItem>
          ))}
        </TimelineContainer>
      </Section>

      <Section 
        title="Education" 
        subtitle="Academic qualifications and formal training in computer science"
      >
        <TimelineContainer>
          {education.map((edu, index) => (
            <TimelineItem
              key={edu.id}
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <TimelineContent>
                <TimelineHeader>
                  <div>
                    <TimelineTitle>{edu.degree} in {edu.field}</TimelineTitle>
                    <TimelineSubtitle>{edu.institution}</TimelineSubtitle>
                  </div>
                  <TimelineDate>
                    <GraduationCap size={16} />
                    {edu.startDate} - {edu.isCurrentlyStudying ? 'Present' : edu.endDate}
                  </TimelineDate>
                </TimelineHeader>
                
                {edu.gpa && (
                  <div style={{ marginBottom: theme.spacing.md }}>
                    <strong>GPA:</strong> {edu.gpa}
                  </div>
                )}

                {edu.description && edu.description.length > 0 && (
                  <TimelineDescription>
                    {edu.description.map((item, idx) => (
                      <TimelineDescItem key={idx}>{item}</TimelineDescItem>
                    ))}
                  </TimelineDescription>
                )}

                {edu.honors && edu.honors.length > 0 && (
                  <TechnologiesContainer>
                    {edu.honors.map((honor, honorIdx) => (
                      <Badge key={honorIdx} variant="accent">
                        <Award size={12} />
                        {honor}
                      </Badge>
                    ))}
                  </TechnologiesContainer>
                )}
              </TimelineContent>
            </TimelineItem>
          ))}
        </TimelineContainer>
      </Section>

      <Section title="Technical Skills" subtitle="Core competencies and technology stack proficiency">
        <SkillsGrid>
          {Object.entries(skillsByCategory).map(([category, categorySkills]) => (
            <SkillCategory key={category}>
              <SkillCategoryTitle>
                <Code size={20} />
                {category}
              </SkillCategoryTitle>
              <SkillsList>
                {categorySkills.map((skill) => (
                  <SkillItem key={skill.id}>
                    <SkillName>{skill.name}</SkillName>
                    <SkillLevel level={skill.level}>
                      {skill.level}
                    </SkillLevel>
                  </SkillItem>
                ))}
              </SkillsList>
            </SkillCategory>
          ))}
        </SkillsGrid>
      </Section>

      <Section title="Languages" subtitle="Multilingual communication capabilities">
        <LanguagesGrid>
          {languages.map((language, index) => (
            <LanguageCard
              key={language.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <LanguageName>{language.name}</LanguageName>
              <LanguageLevel>{language.level}</LanguageLevel>
              {language.description && (
                <LanguageDescription>{language.description}</LanguageDescription>
              )}
            </LanguageCard>
          ))}
        </LanguagesGrid>
      </Section>

      <Section title="Personal Interests" subtitle="Professional development and extracurricular pursuits">
        <InterestsGrid>
          {interests.map((interest, index) => (
            <InterestCard
              key={interest.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <InterestName>{interest.name}</InterestName>
              {interest.description && (
                <InterestDescription>{interest.description}</InterestDescription>
              )}
            </InterestCard>
          ))}
        </InterestsGrid>
      </Section>
    </>
  );
};